import { useMemo, useCallback, useRef, useEffect, useState } from 'react';
import noop from '@picsart/rc/noop';
import { useTranslator } from '@picsart/rc/services/localization';
import { Button, ButtonSizeSM } from '@picsart/button';
import { useNotifier } from '@picsart/notifier/Notifier';
import { NotifierDuration, ToastType } from '@picsart/notifier/types';
import { NotificationStatuses } from '@picsart/notifier';

import { PREMIUM_FEATURES } from 'configs/constants';
import useMemoSelector from 'hooks/useMemoSelector';
import { getUserData } from 'store/selectors/general';
import useSubscribe from 'hooks/useSubscribe';

interface IMessageText {
  title: string;
  contentBeforeLink: string;
}

const usePremiumToaster = (message: IMessageText | null = null) => {
  const translate = useTranslator();
  const [showToaster, setShowToaster] = useState(false);
  const { addNotification } = useNotifier();
  const {
    isSubscribed,
    isTrialUsed,
    isProFeatureEnabledForLocation,
    waitingUserSubscription,
  } = useMemoSelector(getUserData);

  const { subscribe } = useSubscribe();

  const removeNotification = useRef(noop);

  const getMessageText = useMemo(() => {
    if (message !== null) {
      return message;
    }

    if (!isProFeatureEnabledForLocation) {
      return isTrialUsed ? PREMIUM_FEATURES.default : PREMIUM_FEATURES.trialUnUsed;
    }

    return isTrialUsed ? PREMIUM_FEATURES.proTrialUsed : PREMIUM_FEATURES.proWithTrial;
  }, [isProFeatureEnabledForLocation, isTrialUsed, message]);

  const { title, contentBeforeLink } = getMessageText;

  const onUpgradeClick = useCallback(() => {
    subscribe();
    removeNotification.current();
  }, [subscribe]);

  useEffect(() => {
    if (showToaster && !waitingUserSubscription && !isSubscribed) {
      addNotification({
        toastType: ToastType.Notification,
        status: NotificationStatuses.Premium,
        title: translate(title),
        description: translate(contentBeforeLink),
        duration: NotifierDuration.Notification,
        actions: [
          <Button
            onClick={onUpgradeClick}
            size={ButtonSizeSM}
          >
            {translate(isTrialUsed ? 'Upgrade' : 'Start free trial')}
          </Button>,
        ],
      });
      setShowToaster(false);
    }
  }, [title, translate, showToaster, waitingUserSubscription, isSubscribed, isProFeatureEnabledForLocation, addNotification, contentBeforeLink, onUpgradeClick, isTrialUsed]);

  return useCallback(() => {
    // make showToaster true, so if waitingUserSubscription is true, it'll wait for it to become false and then show the toaster with right data
    setShowToaster(true);
  }, []);
};

export default usePremiumToaster;
